import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logo, arrowIcon } from '../assets';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  // Scroll-based navbar visibility logic
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // On home page, always hide navbar when scrolling
      if (location.pathname === '/') {
        if (currentScrollY > 30) {
          // Hide navbar completely on home page when scrolling
          setIsNavbarVisible(false);
        } else {
          // Show navbar only at top of home page
          setIsNavbarVisible(true);
        }
      } else {
        // On other pages, show navbar when scrolling up, hide when scrolling down
        if (currentScrollY > lastScrollY && currentScrollY > 80) {
          // Scrolling down and past initial 80px - hide navbar
          setIsNavbarVisible(false);
        } else if (currentScrollY < lastScrollY) {
          // Scrolling up - show navbar
          setIsNavbarVisible(true);
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, location.pathname]);

  // Home page navbar blur effect when scrolling
  const isHomePage = location.pathname === '/';
  const shouldAddBlur = isHomePage && lastScrollY > 30;

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 h-[124px] w-full z-50 ${
        !isNavbarVisible ? '-translate-y-full transition-transform duration-300' : 'transition-transform duration-300'
      } ${
        isHomePage 
          ? (shouldAddBlur ? 'backdrop-blur-[10px] bg-[#1a1e23]/20' : 'bg-[#1a1e23]/0')
          : 'bg-[#1a1e23]'
      }`}
    >
      <div className="w-full h-full px-[36px] sm:px-8 lg:px-[60px] flex items-center justify-between pt-[20px] pb-[20px]">
        {/* Logo */}
        <Link to="/" className="h-[85px] w-[247px] sm:h-[92px] sm:w-[273px] lg:h-[100px] lg:w-[299px] bg-contain bg-center bg-no-repeat flex-shrink-0" style={{ backgroundImage: `url('${logo}')` }} />
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-16">
          <nav className="flex gap-12 items-center">
            <Link to="/about" className={`text-[18px] font-normal leading-[0] transition-colors whitespace-nowrap ${
              location.pathname === '/about' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
            }`}>
              About Us
            </Link>
            <Link to="/events" className={`text-[18px] font-normal leading-[0] transition-colors whitespace-nowrap ${
              location.pathname === '/events' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
            }`}>
              Events
            </Link>
            <Link to="/calendar" className={`text-[18px] font-normal leading-[0] transition-colors whitespace-nowrap ${
              location.pathname === '/calendar' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
            }`}>
              Event Calendar
            </Link>
            <Link to="/gallery" className={`text-[18px] font-normal leading-[0] transition-colors whitespace-nowrap ${
              location.pathname === '/gallery' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
            }`}>
              Gallery
            </Link>
            <Link to="/membership" className={`text-[18px] font-normal leading-[0] transition-colors whitespace-nowrap ${
              location.pathname === '/membership' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
            }`}>
              Membership
            </Link>
          </nav>
          
          {/* Join Now Button with CSS Box-Shadow and Shimmer */}
          <Link 
            to="/join" 
            className="bg-[#c6d042] text-[#1a1e23] px-[30px] py-3.5 rounded-[60px] font-bold text-[16px] inline-flex items-center gap-[11px] relative overflow-hidden group transition-all duration-500 ease-out whitespace-nowrap hover:scale-[1.02] hover:shadow-[0_0_12px_rgba(197,207,66,0.3),0_0_20px_rgba(255,107,107,0.2),0_0_28px_rgba(78,205,196,0.15)] hover:bg-gradient-to-r hover:from-[#c6d042] hover:via-[#d4e04a] hover:to-[#c6d042] before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:translate-x-[-100%] group-hover:before:translate-x-[100%] before:transition-transform before:duration-800 before:ease-out after:absolute after:inset-[-4px] after:rounded-[64px] after:bg-gradient-to-r after:from-[#ff6b6b] after:via-[#4ecdc4] after:via-[#45b7d1] after:via-[#96ceb4] after:to-[#feca57] after:opacity-0 group-hover:after:opacity-100 after:transition-all after:duration-600 after:ease-out after:z-[-1]"
          >
            <span>Join Now</span>
            <img src={arrowIcon} alt="Arrow icon" className="w-5 h-5" />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden flex flex-col justify-center items-center w-8 h-8 text-[#f4f2e5]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
          <span className={`block w-6 h-0.5 bg-current transition-all duration-300 mt-1 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`block w-6 h-0.5 bg-current transition-all duration-300 mt-1 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
        </button>

        {/* Mobile Navigation */}
        <div className={`fixed inset-0 bg-[#1a1e23] z-50 lg:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
            <div className={`flex flex-col h-full px-[36px] py-8 transition-all duration-300 ease-in-out ${
              isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}>
              {/* Header with Logo and Close Button */}
              <div className="flex items-center justify-between mb-12">
                {/* Logo */}
                <Link to="/" className="h-[85px] w-[247px] bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url('${logo}')` }} onClick={() => setIsMobileMenuOpen(false)} />
                
                {/* Close Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-[#f4f2e5] text-2xl font-light"
                  aria-label="Close menu"
                >
                  ×
                </button>
              </div>
              
              {/* Navigation Links */}
              <div className="flex-1 flex flex-col justify-start pt-8 space-y-6">
                <Link 
                  to="/about" 
                  className={`text-[24px] font-bold transition-all duration-300 ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    location.pathname === '/about' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? '0.1s' : '0s' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link 
                  to="/events" 
                  className={`text-[24px] font-bold transition-all duration-300 ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    location.pathname === '/events' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? '0.2s' : '0s' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Events
                </Link>
                <Link 
                  to="/calendar" 
                  className={`text-[24px] font-bold transition-all duration-300 ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    location.pathname === '/calendar' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? '0.3s' : '0s' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Event Calendar
                </Link>
                <Link 
                  to="/gallery" 
                  className={`text-[24px] font-bold transition-all duration-300 ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    location.pathname === '/gallery' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? '0.35s' : '0s' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Gallery
                </Link>
                <Link 
                  to="/membership" 
                  className={`text-[24px] font-bold transition-all duration-300 ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    location.pathname === '/membership' ? 'text-[#c6d042]' : 'text-[#f4f2e5] hover:text-[#c6d042]'
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? '0.4s' : '0s' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Membership
                </Link>
                
                {/* CTA Button */}
                <div className="flex justify-start mt-4">
                  <Link 
                    to="/join" 
                    className={`bg-[#c6d042] text-[#1a1e23] px-4 py-3 rounded-full font-bold text-base inline-flex items-center gap-2 ${
                      isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                    }`}
                    style={{ transitionDelay: isMobileMenuOpen ? '0.4s' : '0s' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Join Now
                    <img src={arrowIcon} alt="Arrow icon" className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
      </div>
    </header>
  );
};

export default Header;
